/**
 * Element construction for the Form Builder.
 *
 * Every way a question enters a form — "Add element", the Contact Info and
 * Consent helpers, "Paste screener", Duplicate and "Import questions" — builds
 * it here, so the rules for a new element (fresh `id`, uuid-suffixed
 * `field_name`, default options, which source properties carry over) live in
 * one place. Moved verbatim out of `Preview.create()` / `Toolbar` so code
 * outside the builder (the Question Bank) can build elements the same way.
 */
import cloneDeep from 'lodash/cloneDeep';
import helpersScreener from 'utils/helpers-screener';
import ID from './UUID';

/**
 * Default options for a new element of the given type: two blank rows for the
 * choice types, two rows + two columns for a Matrix, nothing otherwise.
 *
 * @param {string} element  element type key, e.g. `RadioButtons`
 * @returns {Array<Object>}
 */
export function defaultItemOptions(element) {
    /*

        WARNINGS, NOTES, ETC

        Originally added the "option.type" key to support the matrix (so there can be columns & rows).
        When we added the ability to change an element to another (e.g. from a checkbox to a matrix)
            i added the "option.type" to all other types with options.

  */

    switch (element) {
        case 'Dropdown':
            return [
                { value: ID.uuid(), text: '', key: `option_${ID.uuid()}`, type: 'row' },
                { value: ID.uuid(), text: '', key: `option_${ID.uuid()}`, type: 'row' }
            ];
        case 'Tags':
            return [
                {
                    value: 'place_holder_tag_1',
                    text: 'Place holder tag 1',
                    key: `option_${ID.uuid()}`,
                    type: 'row'
                },
                { value: 'place_holder_tag_2', text: 'Place holder tag 2', key: `option_${ID.uuid()}`, type: 'row' }
            ];
        case 'Checkboxes':
            return [
                { value: ID.uuid(), text: '', key: `option_${ID.uuid()}`, type: 'row' },
                { value: ID.uuid(), text: '', key: `option_${ID.uuid()}`, type: 'row' }
            ];
        case 'Matrix':
            return [
                { value: '', text: '', key: `option_${ID.uuid()}`, type: 'row' },
                { value: '', text: '', key: `option_${ID.uuid()}`, type: 'row' },
                { value: '', text: '', key: `option_${ID.uuid()}`, type: 'column' },
                { value: '', text: '', key: `option_${ID.uuid()}`, type: 'column' }
            ];
        case 'RadioButtons':
            return [
                { value: ID.uuid(), text: '', key: `option_${ID.uuid()}`, type: 'row' },
                { value: ID.uuid(), text: '', key: `option_${ID.uuid()}`, type: 'row' }
            ];
        default:
            return [];
    }
}

/**
 * Build a new form element from an element template (an entry of
 * `config/available-elements.js`).
 *
 * @param {Object} item  element template
 * @param {Object} [itemDefaults]  values to start from: the source question when
 *   duplicating/importing, or helper defaults (label, options, required, …).
 *   Only a fixed set of properties is copied; see below.
 * @param {string} [customColumnLabel]  panel column title to map the element to
 *   (two-way sync), as used by the Contact Info helper
 * @param {Object} [context]
 * @param {boolean} [context.showDescription]  builder-level "show description" default
 * @param {Array<{id: *, title: string}>} [context.availableColumns]  the account's panel columns
 * @returns {Object} the new element
 */
export function createElement(item, itemDefaults, customColumnLabel, context = {}) {
    const { showDescription, availableColumns = [] } = context;
    const elementOptions = {
        id: ID.uuid(),
        element: item.element || item.key,
        text: item.name,
        static: item.static,
        required: true,
        save_to_panel: false,
        showDescription: item.showDescription,
        personCustomDataId: '',
        question_logic: false,
        question_logic_rules: helpersScreener.getQuestionLogicRulesDefault()
    };

    if (showDescription === true && !item.static) {
        elementOptions.showDescription = true;
    }

    if (item.static) {
        elementOptions.bold = false;
        elementOptions.italic = false;
    }

    if (item.canHaveAnswer) {
        elementOptions.canHaveAnswer = item.canHaveAnswer;
    }

    if (item.canReadOnly) {
        elementOptions.readOnly = false;
    }

    if (item.canDefaultToday) {
        elementOptions.defaultToday = false;
    }

    if (item.hasOwnProperty('content')) {
        elementOptions.content = item.content;
    }

    if (item.href) {
        elementOptions.href = item.href;
    }

    elementOptions.canHavePageBreakBefore = item.canHavePageBreakBefore !== false;
    elementOptions.canHaveAlternateForm = item.canHaveAlternateForm !== false;
    elementOptions.canHaveDisplayHorizontal = item.canHaveDisplayHorizontal !== false;
    elementOptions.canHaveOptionCorrect = item.canHaveOptionCorrect !== false;
    elementOptions.canHaveOptionValue = item.canHaveOptionValue !== false;
    elementOptions.canPopulateFromApi = item.canPopulateFromApi !== false;

    if (item.key === 'Image') {
        elementOptions.src = item.src;
    }

    if (item.key === 'Checkboxes' || item.key === 'RadioButtons') {
        elementOptions.num_columns = item.num_columns;
    }

    if (item.key === 'DatePicker') {
        elementOptions.dateFormat = item.dateFormat;
        elementOptions.timeFormat = item.timeFormat;
        elementOptions.showTimeSelect = item.showTimeSelect;
        elementOptions.showTimeSelectOnly = item.showTimeSelectOnly;
    }

    if (item.key === 'AddPipeInput') {
        elementOptions.video_recording_time = item.video_recording_time;
    }

    if (item.key === 'FileUpload') {
        elementOptions.upload_files_cnt = item.upload_files_cnt;
    }

    if (item.key === 'Download') {
        elementOptions._href = item._href;
        elementOptions.file_path = item.file_path;
    }

    // hidden values should not be required
    if (item.key === 'HiddenValue') {
        elementOptions.required = false;
    }

    if (item.key === 'RangeElement') {
        elementOptions.step = item.step;
        elementOptions.min_value = item.min_value;
        elementOptions.max_value = item.max_value;
        elementOptions.min_label = item.min_label;
        elementOptions.max_label = item.max_label;
    }

    if (item.default_value !== undefined) {
        elementOptions.default_value = item.default_value;
    }

    if (item.defaultValue) {
        elementOptions.defaultValue = item.defaultValue;
    }

    if (item.field_name) {
        elementOptions.field_name = item.field_name + ID.uuid();
    }

    if (item.hasOwnProperty('label')) {
        elementOptions.label = item.label;
    }

    if (item.options) {
        if (item.options.length > 0) {
            elementOptions.options = item.options;
        } else {
            elementOptions.options = defaultItemOptions(elementOptions.element);
        }
    }

    if (item.option_order !== undefined) {
        elementOptions.option_order = item.option_order;
    }

    if (item.qual_disqual) {
        elementOptions.qual_disqual = item.qual_disqual;
    }

    /* opportunity to pass in defaults */

    if (itemDefaults) {
        if (elementOptions.static) {
            elementOptions.content = itemDefaults.content
                ? itemDefaults.content
                : itemDefaults.label
                ? itemDefaults.label
                : '';
        } else {
            elementOptions.label = itemDefaults.label
                ? itemDefaults.label
                : itemDefaults.content
                ? itemDefaults.content
                : '';

            if (itemDefaults.hasOwnProperty('options')) {
                elementOptions.options = itemDefaults.options;
            }

            if (itemDefaults.hasOwnProperty('other_specify_label')) {
                elementOptions.other_specify_label = itemDefaults.other_specify_label;
            }

            if (itemDefaults.hasOwnProperty('none_above_specify_label')) {
                elementOptions.none_above_specify_label = itemDefaults.none_above_specify_label;
            }

            if (itemDefaults.hasOwnProperty('add_other')) {
                elementOptions.add_other = itemDefaults.add_other;
            }

            if (itemDefaults.hasOwnProperty('none_of_the_above')) {
                elementOptions.none_of_the_above = itemDefaults.none_of_the_above;
            }

            if (itemDefaults.hasOwnProperty('option_order')) {
                elementOptions.option_order = itemDefaults.option_order;
            }

            if (itemDefaults.hasOwnProperty('qual_disqual')) {
                elementOptions.qual_disqual = itemDefaults.qual_disqual;
            }

            if (itemDefaults.hasOwnProperty('required')) {
                elementOptions.required = itemDefaults.required;
            }

            if (itemDefaults.hasOwnProperty('num_columns')) {
                elementOptions.num_columns = itemDefaults.num_columns;
            }

            if (itemDefaults.hasOwnProperty('validation')) {
                elementOptions.validation = itemDefaults.validation;
            }

            if (itemDefaults.hasOwnProperty('validation_rules')) {
                elementOptions.validation_rules = itemDefaults.validation_rules;
            }

            if (itemDefaults.hasOwnProperty('reporting_values')) {
                elementOptions.reporting_values = itemDefaults.reporting_values;
            }

            if (itemDefaults.hasOwnProperty('question_logic')) {
                elementOptions.question_logic = itemDefaults.question_logic;
            }

            if (itemDefaults.hasOwnProperty('question_logic_rules')) {
                elementOptions.question_logic_rules = itemDefaults.question_logic_rules;
            }

            if (itemDefaults.hasOwnProperty('save_to_panel')) {
                elementOptions.save_to_panel = itemDefaults.save_to_panel;
            }

            if (itemDefaults.hasOwnProperty('personCustomDataId')) {
                elementOptions.personCustomDataId = itemDefaults.personCustomDataId;
            }

            if (itemDefaults.hasOwnProperty('max_label')) {
                elementOptions.max_label = itemDefaults.max_label;
            }

            if (itemDefaults.hasOwnProperty('min_label')) {
                elementOptions.min_label = itemDefaults.min_label;
            }

            if (itemDefaults.hasOwnProperty('step')) {
                elementOptions.step = itemDefaults.step;
            }

            if (itemDefaults.hasOwnProperty('min_value')) {
                elementOptions.min_value = itemDefaults.min_value;
            }

            if (itemDefaults.hasOwnProperty('max_value')) {
                elementOptions.max_value = itemDefaults.max_value;
            }

            if (itemDefaults.hasOwnProperty('default_value')) {
                elementOptions.default_value = itemDefaults.default_value;
            }
        }
    }

    if (customColumnLabel) {
        elementOptions.save_to_panel = true;

        let panelMapColumn = availableColumns.find(ac => ac.title == customColumnLabel);
        if (panelMapColumn) {
            elementOptions.personCustomDataId = panelMapColumn.id;
        }
    }

    // `itemDefaults` is the source element when a question is duplicated or
    // imported from another screener; normalizing here stops stringified
    // numeric bounds on that source from propagating into the new element.
    return helpersScreener.normalizeNumericQuestionProps(elementOptions);
}

/**
 * Copies of `item.options` with a fresh uuid appended to every `key`, as used
 * when a question is duplicated or imported. The source key is kept as a
 * prefix. Returns `[]` when the item has no options.
 *
 * @param {{ options?: Array<Object> }} item
 * @returns {Array<Object>}
 */
export function copyOptionsWithNewKeys(item) {
    const allNewOptions = [];
    if (item.options) {
        item.options.forEach(option => {
            const copiedOption = cloneDeep(option);
            copiedOption.key = copiedOption.key + ID.uuid();
            allNewOptions.push(copiedOption);
        });
    }

    return allNewOptions;
}

/**
 * The element template for an element type key, or undefined.
 *
 * @param {Array<Object>} templates  e.g. `config/available-elements.js`
 * @param {string} elementKey
 * @returns {Object|undefined}
 */
export function findElementTemplate(templates, elementKey) {
    return templates.find(item => item.key == elementKey);
}
